import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxDropDownBoxModule, DxFormComponent, DxTagBoxModule, DxTextBoxModule, DxValidatorComponent, DxValidatorModule } from 'devextreme-angular';
import { DkDxDataCtlTableComponent } from '../../dk-dx-data-ctl-table/dk-dx-data-ctl-tablecomponent';
import { TagBoxValuePipePipe } from '../../../pipes/tag-box-value-pipe/tagBoxValuePipe.pipe';
import { Subject, takeUntil } from 'rxjs';
import { ControlConditionModel } from '../../../models/gen-control.model';
import { OrtakService } from '../../../services/ortak.service';
import { CtlGenDropBoxMultiKontrolService } from './ctl-gen-drop-box-multi-kontrol.service';
import { DkGlobalUserSettingsService } from '../../../services/dk-global-user-settings.service';
import { FormLabelMode } from 'devextreme/ui/form';
import DataSource from 'devextreme/data/data_source';
import { DxTagBoxTypes } from 'devextreme-angular/ui/tag-box';

@Component({
  selector: 'dk-ctl-gen-drop-box-multi-kontrol',
  standalone: true,
  imports: [CommonModule, DxDropDownBoxModule, DxTagBoxModule, DxTextBoxModule, DkDxDataCtlTableComponent, TagBoxValuePipePipe, DxValidatorModule],
  providers: [CtlGenDropBoxMultiKontrolService],
  templateUrl: './ctl-gen-drop-box-multi-kontrol.component.html',
  styleUrls: ['./ctl-gen-drop-box-multi-kontrol.component.scss'],
})
export class CtlGenDropBoxMultiKontrolComponent implements OnInit, OnChanges, OnDestroy {
  @Input() controlName = '';
  @Input() labelText = '';
  @Input() placeholder = '';
  @Input() labelMode: FormLabelMode = this.globalSettings.globalLabelMode;
  @Input() controlCondition: ControlConditionModel[];
  @Input() customQuery = '';
  @Input() isReadOnly = false;
  @Input() selectedKeys: any[] = [];
  @Input() queryParams: { [key: string]: any } = {};
  @Input() tagBox = false;
  @Input() maxDisplayedTags = 3;
  @Input() showSelectionControls = true;
  @Input() validationRules: any[] | null = [];
  @Input() isRequired:boolean = false;

  @Output() selectedKeysChange = new EventEmitter<any[]>();
  @Output() resultValidation = new EventEmitter<any>();

  control: any;
  columns: any[] = [];
  dataSource: DataSource;
  isOpened = false;
  isFormValid: boolean = false;
  displayField = '';

  @ViewChild(DxFormComponent, {static: false}) form: DxFormComponent;
  @ViewChild(DxValidatorComponent,{static:false}) validator:DxValidatorComponent;

  private destroy$ = new Subject<void>();

  constructor(
    private service: CtlGenDropBoxMultiKontrolService,
    private ortakService: OrtakService,
    private globalSettings: DkGlobalUserSettingsService,
    private ref: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.controlName) {
      this.setParams();
      this.getControl();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['controlCondition'] || changes['customQuery']) {
      this.setParams();
      if (this.control) {
        this.loadData();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setParams(): void {
    this.service.controlCondition = this.controlCondition;
    this.service.customQuery = this.customQuery;
    this.service.queryParams = this.queryParams;
  }

  getControl(): void {
    this.ortakService
      .getControl(this.controlName)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.control = data?.controlMetadata;
        if (this.labelText === '') this.labelText = this.control?.controlDescription;

        if (this.control?.descriptionField) {
          this.displayField = this.control.descriptionField.split(',')[0].trim();
        }

        // Force multi-selection for this control
        if (this.control?.dlistMetadata) {
          this.control.dlistMetadata.showSelectionYn = true;
          this.control.dlistMetadata.allowSelectionYn = true;
        }

        // Trigger DList metadata load
        this.service.getDlist(this.control?.dlistCode);

        // Map columns when DList metadata arrives
        this.service.gridControlsList$
          .pipe(takeUntil(this.destroy$))
          .subscribe((dlist) => {
            if (dlist) {
              this.columns = this.ortakService.setRecursiveFieldData([dlist], false);
              this.ref.detectChanges();
            }
          });

        if (this.control?.dlistMetadata?.baseEntity) {
          this.loadData();
        }
      });
  }

  loadData(): void {
    if (!this.control) return;
    
    this.setParams();
    this.dataSource = this.service.getData(
      '', 
      null, 
      this.control, 
      'string', 
      '', 
      this.control.kodField
    );
    this.ref.detectChanges();
  }

  onSelectionChanged(keys: any): void {
    if (!this.control) return;
    
    let newKeys: any[] = [];
    if (Array.isArray(keys)) {
      newKeys = keys;
    } else if (keys && Array.isArray(keys.selectedRowKeys)) {
      newKeys = keys.selectedRowKeys;
    } else if (keys && Array.isArray(keys.value)) {
      newKeys = keys.value;
    }

    // JSON comparison is a safe way to check for deep equality in simple arrays
    if (JSON.stringify(this.selectedKeys) === JSON.stringify(newKeys)) {
      return;
    }

    this.selectedKeys = [...newKeys];
    this.selectedKeysChange.emit(this.selectedKeys);
    this.onFormChange(this.selectedKeys);
  }

  onFormChange(value: any) {
    if (value !== undefined && value !== null) {
      this.isFormValid = this.validator?.instance?.validate().status === 'valid';
    }
    this.resultValidation.emit(this.isFormValid);
  }

  onOptionChanged(e: any): void {
    if (e.name === 'value' && (!e.value || e.value.length === 0)) {
      this.selectedKeys = [];
      this.selectedKeysChange.emit([]);
      this.onFormChange([]);
    }
  }

  displayExpr = (keys: any): string => {
    if (!keys) return '';
    if (Array.isArray(keys)) return keys.join(',');
    return keys;
  };

    onMultiTagPreparing(args: DxTagBoxTypes.MultiTagPreparingEvent) {
      // const selectedItemsLength = args.selectedItems.length;
      // const totalCount = 5;
      //
      // if (selectedItemsLength < totalCount) {
      //   args.cancel = true;
      // } else {
      //   args.text = `Seçilen (${selectedItemsLength})`;
      // }
    }
}
